import React, { useEffect, useState } from 'react';
import { BsPencil } from 'react-icons/bs';
import { FaXmark } from 'react-icons/fa6';
import { Button } from 'react-bootstrap';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../../../../../../shared/functions/Functions';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../../../../store/rootReducer';

interface IHeaderProps {
    status?: string;
    createdAt: string;
    contractItemId: number;
    acceptedDate?: string;
    purchaseOrderInfluencer?: string;
    onSavePurchaseOrder?: (value: string) => Promise<boolean>;
}

export function ContractHeader({
    createdAt,
    contractItemId,
    acceptedDate,
    status,
    purchaseOrderInfluencer,
    onSavePurchaseOrder,
}: IHeaderProps) {
    const { t } = useTranslation();
    const { isEditable, contractModalStatus, campaign } = useSelector((state: IAllStates) => state.contractBrand);
    const isAccepted = status === 'accepted' || status === 'completed';
    const [po, setPo] = useState(purchaseOrderInfluencer ?? '');
    const [isEditingPo, setIsEditingPo] = useState(false);
    const [draft, setDraft] = useState('');

    useEffect(() => {
        setPo(purchaseOrderInfluencer ?? '');
    }, [purchaseOrderInfluencer]);

    const savePo = async () => {
        const value = draft.trim();
        if (onSavePurchaseOrder && (await onSavePurchaseOrder(value))) {
            setPo(value);
            setIsEditingPo(false);
        }
    };

    return (
        <div className="d-flex justify-content-between">
            <p className="text-muted mb-0">
                Contract ID: #{contractItemId}
                {isAccepted && (
                    <>
                        {' - '}
                        {isEditingPo ? (
                            <span className="d-inline-flex align-items-center">
                                <span className="mr-2">PO:</span>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    placeholder="Enter PO"
                                    value={draft}
                                    autoFocus
                                    onChange={e => setDraft(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            savePo();
                                        }
                                        if (e.key === 'Escape') setIsEditingPo(false);
                                    }}
                                />
                                <Button
                                    className="mx-2"
                                    variant="outline-secondary"
                                    onClick={() => setIsEditingPo(false)}
                                >
                                    <FaXmark className="text-danger" size={18} />
                                </Button>
                                <Button variant="outline-secondary" onClick={savePo}>
                                    <img width={15} alt="Confirm icon" src="/icons/img-switch-check.png" />
                                </Button>
                            </span>
                        ) : (
                            <>
                                PO: <span className="text-primary">{po || '-'}</span>
                                {onSavePurchaseOrder && (
                                    <BsPencil
                                        className="ml-2 text-primary"
                                        role="button"
                                        onClick={() => {
                                            setDraft(po);
                                            setIsEditingPo(true);
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </>
                )}
            </p>
            <div className="d-flex justify-content-center">
                <div>
                    {campaign?.contracts[0]?.isRequested && (
                        <p className="mr-2 status invite text-dark">{t(`general.campaign_status.invite`)}</p>
                    )}
                </div>
                {contractModalStatus !== 'negotiate' && (
                    <p className="text-muted text-end mb-0">
                        {(status === 'accepted' || status === 'completed') && acceptedDate ? (
                            <>
                                {t('brand.campaign.book.offer.modal.accepted_at')} – {formatDate(acceptedDate)} –{' '}
                                {acceptedDate.substring(11, 16)}
                            </>
                        ) : (
                            <>
                                {isEditable
                                    ? t('brand.campaign.book.offer.modal.received_at')
                                    : t('brand.campaign.book.offer.modal.sent_at')}
                                {' – '}
                                {formatDate(createdAt)} – {createdAt.substring(11, 16)}
                            </>
                        )}
                    </p>
                )}
            </div>
        </div>
    );
}
